import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, LayoutDashboard, CalendarClock, CalendarCheck, Mic2, Megaphone } from "lucide-react";

// ── Types ────────────────────────────────────────────────────────────────────

interface HomepageSection {
  id: number;
  section_key: string;
  eyebrow: string | null;
  title: string | null;
  subtitle: string | null;
  badge_text: string | null;
  heading: string | null;
  body_text: string | null;
  cta_primary_label: string | null;
  cta_primary_href: string | null;
  cta_secondary_label: string | null;
  cta_secondary_href: string | null;
  is_visible: boolean;
  sort_order: number;
}

type SectionMap = Record<string, HomepageSection>;

// ── Helpers ──────────────────────────────────────────────────────────────────

function str(v: string | null | undefined): string {
  return v ?? "";
}

// ── Section sub-editors ───────────────────────────────────────────────────────

interface SectionEditorProps {
  section: HomepageSection;
  saving: boolean;
  onChange: (key: string, field: keyof HomepageSection, value: string | boolean | null) => void;
  onSave: (key: string) => void;
}

/** Two-column field row */
function FieldRow({ label, id, children }: { label: string; id: string; children: React.ReactNode }) {
  return (
    <div className="grid sm:grid-cols-[180px_1fr] gap-2 items-start">
      <Label htmlFor={id} className="pt-2 text-sm font-medium text-muted-foreground sm:text-right">
        {label}
      </Label>
      <div>{children}</div>
    </div>
  );
}

function UpcomingEventsEditor({ section, saving, onChange, onSave }: SectionEditorProps) {
  const k = section.section_key;
  return (
    <div className="space-y-4">
      <FieldRow label="Eyebrow label" id={`${k}-eyebrow`}>
        <Input
          id={`${k}-eyebrow`}
          value={str(section.eyebrow)}
          onChange={(e) => onChange(k, "eyebrow", e.target.value)}
          placeholder="e.g. Coming soon"
        />
      </FieldRow>
      <FieldRow label="Section title" id={`${k}-title`}>
        <Input
          id={`${k}-title`}
          value={str(section.title)}
          onChange={(e) => onChange(k, "title", e.target.value)}
          placeholder="e.g. Upcoming Events"
        />
      </FieldRow>
      <FieldRow label="Subtitle" id={`${k}-subtitle`}>
        <Input
          id={`${k}-subtitle`}
          value={str(section.subtitle)}
          onChange={(e) => onChange(k, "subtitle", e.target.value)}
          placeholder="Short description shown below the title"
        />
      </FieldRow>
      <FieldRow label="Visible" id={`${k}-visible`}>
        <Switch
          id={`${k}-visible`}
          checked={section.is_visible}
          onCheckedChange={(checked) => onChange(k, "is_visible", checked)}
        />
      </FieldRow>
      <div className="flex justify-end pt-2">
        <Button onClick={() => onSave(k)} disabled={saving}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

function PastEventsEditor({ section, saving, onChange, onSave }: SectionEditorProps) {
  const k = section.section_key;
  return (
    <div className="space-y-4">
      <FieldRow label="Eyebrow label" id={`${k}-eyebrow`}>
        <Input
          id={`${k}-eyebrow`}
          value={str(section.eyebrow)}
          onChange={(e) => onChange(k, "eyebrow", e.target.value)}
          placeholder="e.g. Earlier conferences"
        />
      </FieldRow>
      <FieldRow label="Section title" id={`${k}-title`}>
        <Input
          id={`${k}-title`}
          value={str(section.title)}
          onChange={(e) => onChange(k, "title", e.target.value)}
          placeholder="e.g. Past Events"
        />
      </FieldRow>
      <FieldRow label="Subtitle" id={`${k}-subtitle`}>
        <Input
          id={`${k}-subtitle`}
          value={str(section.subtitle)}
          onChange={(e) => onChange(k, "subtitle", e.target.value)}
          placeholder="Short description shown below the title"
        />
      </FieldRow>
      <FieldRow label="Visible" id={`${k}-visible`}>
        <Switch
          id={`${k}-visible`}
          checked={section.is_visible}
          onCheckedChange={(checked) => onChange(k, "is_visible", checked)}
        />
      </FieldRow>
      <div className="flex justify-end pt-2">
        <Button onClick={() => onSave(k)} disabled={saving}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

function SpeakersSectionEditor({ section, saving, onChange, onSave }: SectionEditorProps) {
  const k = section.section_key;
  return (
    <div className="space-y-4">
      <FieldRow label="Eyebrow badge" id={`${k}-badge`}>
        <Input
          id={`${k}-badge`}
          value={str(section.badge_text)}
          onChange={(e) => onChange(k, "badge_text", e.target.value)}
          placeholder="e.g. Featured Voices"
        />
      </FieldRow>
      <FieldRow label="Section heading" id={`${k}-heading`}>
        <Input
          id={`${k}-heading`}
          value={str(section.heading)}
          onChange={(e) => onChange(k, "heading", e.target.value)}
          placeholder="e.g. Distinguished Speakers"
        />
      </FieldRow>
      <FieldRow label='"View All" label' id={`${k}-cta-label`}>
        <Input
          id={`${k}-cta-label`}
          value={str(section.cta_primary_label)}
          onChange={(e) => onChange(k, "cta_primary_label", e.target.value)}
          placeholder="e.g. View All Speakers"
        />
      </FieldRow>
      <FieldRow label="Visible" id={`${k}-visible`}>
        <Switch
          id={`${k}-visible`}
          checked={section.is_visible}
          onCheckedChange={(checked) => onChange(k, "is_visible", checked)}
        />
      </FieldRow>
      <div className="flex justify-end pt-2">
        <Button onClick={() => onSave(k)} disabled={saving}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

function CtaSectionEditor({ section, saving, onChange, onSave }: SectionEditorProps) {
  const k = section.section_key;
  return (
    <div className="space-y-4">
      <FieldRow label="Eyebrow badge" id={`${k}-badge`}>
        <Input
          id={`${k}-badge`}
          value={str(section.badge_text)}
          onChange={(e) => onChange(k, "badge_text", e.target.value)}
          placeholder="e.g. Join Us"
        />
      </FieldRow>
      <FieldRow label="Heading" id={`${k}-heading`}>
        <Input
          id={`${k}-heading`}
          value={str(section.heading)}
          onChange={(e) => onChange(k, "heading", e.target.value)}
          placeholder="e.g. Be Part of the Next Event"
        />
      </FieldRow>
      <FieldRow label="Body text" id={`${k}-body`}>
        <Input
          id={`${k}-body`}
          value={str(section.body_text)}
          onChange={(e) => onChange(k, "body_text", e.target.value)}
          placeholder="Short paragraph shown below the heading"
        />
      </FieldRow>

      <Separator />
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Primary button
      </p>

      <FieldRow label="Label" id={`${k}-cta1-label`}>
        <Input
          id={`${k}-cta1-label`}
          value={str(section.cta_primary_label)}
          onChange={(e) => onChange(k, "cta_primary_label", e.target.value)}
          placeholder="e.g. Browse Events"
        />
      </FieldRow>
      <FieldRow label="Link / href" id={`${k}-cta1-href`}>
        <Input
          id={`${k}-cta1-href`}
          value={str(section.cta_primary_href)}
          onChange={(e) => onChange(k, "cta_primary_href", e.target.value)}
          placeholder="e.g. #upcoming-events"
        />
      </FieldRow>

      <Separator />
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Secondary button
      </p>

      <FieldRow label="Label" id={`${k}-cta2-label`}>
        <Input
          id={`${k}-cta2-label`}
          value={str(section.cta_secondary_label)}
          onChange={(e) => onChange(k, "cta_secondary_label", e.target.value)}
          placeholder="e.g. Contact Us"
        />
      </FieldRow>
      <FieldRow label="Link / href" id={`${k}-cta2-href`}>
        <Input
          id={`${k}-cta2-href`}
          value={str(section.cta_secondary_href)}
          onChange={(e) => onChange(k, "cta_secondary_href", e.target.value)}
          placeholder="e.g. /contact"
        />
      </FieldRow>

      <Separator />
      <FieldRow label="Visible" id={`${k}-visible`}>
        <Switch
          id={`${k}-visible`}
          checked={section.is_visible}
          onCheckedChange={(checked) => onChange(k, "is_visible", checked)}
        />
      </FieldRow>
      <div className="flex justify-end pt-2">
        <Button onClick={() => onSave(k)} disabled={saving}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

// ── Section card wrapper ───────────────────────────────────────────────────────

interface SectionCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  children: React.ReactNode;
}

function SectionCard({ icon: Icon, title, description, children }: SectionCardProps) {
  return (
    <Card className="p-6 space-y-5">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
          <Icon className="w-4 h-4 text-primary" />
        </div>
        <div>
          <h2 className="font-semibold text-base text-foreground">{title}</h2>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      <Separator />
      {children}
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function HomepageCMS() {
  const [sections, setSections] = useState<SectionMap>({});
  const [initialLoading, setInitialLoading] = useState(true);
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadSections();
  }, []);

  const loadSections = async () => {
    try {
      const response = await api.get<HomepageSection[]>("/api/homepage-sections");
      if (response.success && response.data) {
        const map: SectionMap = {};
        for (const s of response.data) {
          map[s.section_key] = { ...s, is_visible: Boolean(s.is_visible) };
        }
        setSections(map);
      }
    } catch {
      toast.error("Failed to load homepage sections");
    } finally {
      setInitialLoading(false);
    }
  };

  const handleChange = (
    key: string,
    field: keyof HomepageSection,
    value: string | boolean | null,
  ) => {
    setSections((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const handleSave = async (key: string) => {
    const section = sections[key];
    if (!section) return;

    setSaving((prev) => ({ ...prev, [key]: true }));
    try {
      const payload: Partial<HomepageSection> = {
        eyebrow:             section.eyebrow,
        title:               section.title,
        subtitle:            section.subtitle,
        badge_text:          section.badge_text,
        heading:             section.heading,
        body_text:           section.body_text,
        cta_primary_label:   section.cta_primary_label,
        cta_primary_href:    section.cta_primary_href,
        cta_secondary_label: section.cta_secondary_label,
        cta_secondary_href:  section.cta_secondary_href,
        is_visible:          section.is_visible,
        sort_order:          section.sort_order,
      };

      const response = await api.put<HomepageSection>(
        `/api/homepage-sections/${key}`,
        payload,
      );
      if (response.success) {
        toast.success("Section saved successfully");
        if (response.data) {
          setSections((prev) => ({
            ...prev,
            [key]: { ...response.data!, is_visible: Boolean(response.data!.is_visible) },
          }));
        }
      } else {
        toast.error(response.message || "Failed to save section");
      }
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : "Failed to save section";
      toast.error(msg);
    } finally {
      setSaving((prev) => ({ ...prev, [key]: false }));
    }
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading homepage sections…</p>
      </div>
    );
  }

  const get = (key: string): HomepageSection | null => sections[key] ?? null;

  const editorProps = (key: string): SectionEditorProps | null => {
    const section = get(key);
    if (!section) return null;
    return {
      section,
      saving: saving[key] ?? false,
      onChange: handleChange,
      onSave: handleSave,
    };
  };

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div>
        <h1 className="font-serif text-3xl text-primary mb-1">Homepage CMS</h1>
        <p className="text-muted-foreground">
          Edit every section heading, label, and call-to-action that appears on the public home
          page. Changes go live immediately after saving.
        </p>
      </div>

      {/* Upcoming Events */}
      {editorProps("upcoming_events") && (
        <SectionCard
          icon={CalendarClock}
          title="Upcoming Events section"
          description="Eyebrow label, title, and subtitle shown above the upcoming events grid."
        >
          <UpcomingEventsEditor {...editorProps("upcoming_events")!} />
        </SectionCard>
      )}

      {/* Past Events */}
      {editorProps("past_events") && (
        <SectionCard
          icon={CalendarCheck}
          title="Past Events section"
          description="Eyebrow label, title, and subtitle shown above the past events grid."
        >
          <PastEventsEditor {...editorProps("past_events")!} />
        </SectionCard>
      )}

      {/* Speakers section */}
      {editorProps("speakers_section") && (
        <SectionCard
          icon={Mic2}
          title="Featured Speakers section"
          description="Badge, heading, and 'View All' button label for the speakers carousel."
        >
          <SpeakersSectionEditor {...editorProps("speakers_section")!} />
        </SectionCard>
      )}

      {/* CTA section */}
      {editorProps("cta_section") && (
        <SectionCard
          icon={Megaphone}
          title="Call-to-Action banner"
          description="Full-width promotional banner at the bottom of the home page."
        >
          <CtaSectionEditor {...editorProps("cta_section")!} />
        </SectionCard>
      )}

      {Object.keys(sections).length === 0 && (
        <Card className="p-12 text-center">
          <LayoutDashboard className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">
            No homepage sections found. Run migration 028 on the database first.
          </p>
        </Card>
      )}
    </div>
  );
}
